import React from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronRight } from 'lucide-react';
import { cn } from '../utils/cn';
import { nextSort, sortRows, type TableSort } from '../utils/tableSort';
import { getAppearance } from '../utils/appearance';
import { ILMSTableSkeleton } from './ILMSLoadingState';
import { ILMSEmptyState } from './ILMSEmptyState';
import { ILMSAlert } from './ILMSAlert';
import { ILMSButton } from './ILMSButton';
import { ExportExcel } from '../features/common/ExportExcel';

export interface ILMSTableColumn<T> {
  key: string;
  header: string;
  /** `rowIndex` is the row's position on the current page (0-based) — for a Sl. no.-style column, not a stable record identity. */
  render: (row: T, rowIndex: number) => React.ReactNode;
  /** Shown as the card title on mobile; the first `primary` column wins. */
  primary?: boolean;
  /** Hide this column entirely on the mobile card view (e.g. it duplicates the title). */
  hideOnMobile?: boolean;
  /** Hide this column on narrower desktop widths (below xl) to reduce density. */
  hideOnTablet?: boolean;
  align?: 'left' | 'right' | 'center';
  className?: string;
  /** false = this header can't be clicked to sort (e.g. an actions column). */
  sortable?: boolean;
  /** What to sort by, when the cell's displayed text isn't the right key. */
  sortValue?: (row: T) => string | number | null | undefined;
}

export interface ILMSTableProps<T> {
  columns: ILMSTableColumn<T>[];
  data: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyActionLabel?: string;
  onEmptyAction?: () => void;
  /** Controlled sorting - the parent sorts `data` itself (e.g. across every
   * page of a server list). Without onSortChange the table sorts the rows
   * it was given. */
  sort?: TableSort | null;
  onSortChange?: (sort: TableSort | null) => void;
  /** Shows an "Excel" download above the table, named this. */
  exportTitle?: string;
  /** Every record to export - defaults to the rows given (fine when the
   * table already has them all; a server-paged list passes a loader). */
  exportRows?: () => Promise<T[]>;
  /** The date a row belongs to, for the export's date range. */
  exportDateOf?: (row: T) => string | null | undefined;
}

/**
 * A table on large screens; the same rows reflow to a stacked card list below the
 * `lg` breakpoint. This is enforced with CSS (not JS) so there is no layout flash.
 */
export function ILMSTable<T>({
  columns,
  data,
  rowKey,
  onRowClick,
  isLoading,
  error,
  onRetry,
  emptyTitle = 'No records found.',
  emptyDescription,
  emptyActionLabel,
  onEmptyAction,
  sort: controlledSort,
  onSortChange,
  exportTitle,
  exportRows,
  exportDateOf,
}: ILMSTableProps<T>) {
  // Row height - Compact (default) or Comfortable, on Administration -> Appearance.
  const compact = getAppearance().table_density !== 'comfortable';
  const [ownSort, setOwnSort] = React.useState<TableSort | null>(null);
  const controlled = !!onSortChange;
  const sort = controlled ? controlledSort ?? null : ownSort;
  const rows = React.useMemo(() => (controlled ? data : sortRows(data, ownSort, columns)), [controlled, data, ownSort, columns]);
  const toggleSort = (key: string) => {
    const next = nextSort(sort, key);
    if (controlled) onSortChange?.(next);
    else setOwnSort(next);
  };

  if (isLoading) {
    return <ILMSTableSkeleton rows={6} columns={Math.min(columns.length, 5)} />;
  }

  if (error) {
    return (
      <ILMSAlert tone="critical" title="Unable to load data">
        {error}
        {onRetry && (
          <div className="mt-2">
            <ILMSButton size="sm" variant="outline" onClick={onRetry}>
              Retry
            </ILMSButton>
          </div>
        )}
      </ILMSAlert>
    );
  }

  if (data.length === 0) {
    return (
      <ILMSEmptyState
        title={emptyTitle}
        description={emptyDescription}
        actionLabel={emptyActionLabel}
        onAction={onEmptyAction}
      />
    );
  }

  const primaryCol = columns.find((c) => c.primary) ?? columns[0];
  const secondaryCols = columns.filter((c) => c.key !== primaryCol.key && !c.hideOnMobile);

  return (
    <div>
      {exportTitle && (
        <div className="mb-2 flex justify-end">
          <ExportExcel title={exportTitle} columns={columns} fetchRows={exportRows ?? (async () => rows)} dateOf={exportDateOf} />
        </div>
      )}
      {/* Desktop / tablet table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-ink-300/20">
              {columns.map((col) => {
                const active = sort?.key === col.key ? sort.dir : null;
                const canSort = col.sortable !== false && !!col.header;
                return (
                  <th
                    key={col.key}
                    aria-sort={active === 'asc' ? 'ascending' : active === 'desc' ? 'descending' : undefined}
                    className={cn(
                      'text-label text-ink-500 text-left px-3 leading-snug align-bottom',
                      compact ? 'py-1' : 'py-2.5',
                      col.align === 'right' && 'text-right',
                      col.align === 'center' && 'text-center',
                      col.hideOnTablet && 'hidden xl:table-cell'
                    )}
                  >
                    {canSort ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(col.key)}
                        title="Sort"
                        className={cn(
                          'inline-flex items-center gap-1 hover:text-ink-900 select-none',
                          col.align === 'right' && 'flex-row-reverse',
                          active && 'text-ink-900'
                        )}
                      >
                        <span>{col.header}</span>
                        {active === 'asc' ? <ArrowUp className="h-3.5 w-3.5" /> : active === 'desc' ? <ArrowDown className="h-3.5 w-3.5" /> : <ArrowUpDown className="h-3.5 w-3.5 opacity-30" />}
                      </button>
                    ) : col.header}
                  </th>
                );
              })}
              {onRowClick && <th className="w-10" aria-hidden="true" />}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(
                  'border-b border-ink-300/10 last:border-0',
                  onRowClick && 'cursor-pointer hover:bg-brand-green-50/50'
                )}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={cn(
                      'text-table-text text-ink-700 px-3 align-middle',
                      compact ? 'py-0.5 leading-[1.125rem]' : 'py-3',
                      col.align === 'right' && 'text-right',
                      col.align === 'center' && 'text-center',
                      col.hideOnTablet && 'hidden xl:table-cell',
                      col.className
                    )}
                  >
                    {col.render(row, rowIndex)}
                  </td>
                ))}
                {onRowClick && (
                  <td className="w-10 text-ink-300">
                    <ChevronRight className="h-4 w-4" />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile card list */}
      <ul className="lg:hidden flex flex-col gap-2.5">
        {rows.map((row, rowIndex) => (
          <li
            key={rowKey(row)}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            className={cn(
              'rounded-lg border border-ink-300/20 bg-white p-3.5',
              onRowClick && 'cursor-pointer active:bg-brand-green-50/50'
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="text-card-title min-w-0 truncate">{primaryCol.render(row, rowIndex)}</div>
              {onRowClick && <ChevronRight className="h-4 w-4 text-ink-300 shrink-0 mt-0.5" />}
            </div>
            <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5">
              {secondaryCols.map((col) => (
                <div key={col.key} className="min-w-0">
                  <dt className="text-helper-text text-ink-400">{col.header}</dt>
                  <dd className="text-table-text text-ink-700 truncate">{col.render(row, rowIndex)}</dd>
                </div>
              ))}
            </dl>
          </li>
        ))}
      </ul>
    </div>
  );
}
